import { Entity } from '@backstage/catalog-model';

/**
 * Extracts a value from an entity using a dot-notation path
 * Examples:
 * - 'metadata.annotations["accuris.io/technical-product-manager"]'
 * - 'spec.profile.displayName'
 * - 'metadata.description'
 * - 'metadata.name'
 */
export function extractValueFromEntityPath(entity: Entity, path: string): any {
  if (!entity || !path) {
    return undefined;
  }

  try {
    // Handle annotation paths with bracket notation
    const annotationMatch = path.match(/^metadata\.annotations\["([^"]+)"\]$/);
    if (annotationMatch) {
      const annotationKey = annotationMatch[1];
      return entity.metadata?.annotations?.[annotationKey];
    }

    // Handle regular dot notation paths
    const pathParts = path.split('.');
    let current: any = entity;

    for (const part of pathParts) {
      if (current === null || current === undefined) {
        return undefined;
      }

      if (typeof current === 'object' && part in current) {
        current = current[part];
      } else {
        return undefined;
      }
    }

    return current;
  } catch (_err) {
    return undefined;
  }
}

/**
 * Validates if a path string is a valid entity path format
 */
export function isValidEntityPath(path: string): boolean {
  if (!path || typeof path !== 'string') {
    return false;
  }

  // Check for annotation pattern
  if (path.match(/^metadata\.annotations\["[^"]+"\]$/)) {
    return true;
  }

  // Check for valid dot notation (no empty parts, no special characters)
  const parts = path.split('.');
  return parts.length > 0 && parts.every(part =>
    part.length > 0 && /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(part)
  );
}